/**
 * ReportContributionFAB — a persistent, floating call-to-action that
 * appears on every /weather/* and /nature/* route.
 *
 * Behaviour:
 *   • Only visible on Weather and Nature route trees (not on the
 *     rest of the app).
 *   • Two-in-one FAB: primary button routes to the most relevant
 *     posting flow for the current section (weather report / nature
 *     ID); an expand chevron reveals both options.
 *   • Bottom-right on desktop, bottom-center on mobile so it doesn't
 *     overlap thumb-reachable bottom-nav elements.
 */
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CloudRain, Camera, Plus, X, Sun, Leaf, Mic } from "lucide-react";

export default function ReportContributionFAB() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const isWeather = /^\/weather(\/|$)/.test(pathname);
  const isNature  = /^\/nature(\/|$)/.test(pathname);
  if (!isWeather && !isNature) return null;
  // Don't stack the FAB on top of the very form it points to.
  if (pathname === "/nature/identify") return null;

  // Primary action depends on section.
  const primaryLabel = isWeather ? "Report Local Weather" : "Identify wildlife";
  const primaryIcon  = isWeather ? CloudRain : Camera;
  const goPrimary = () => {
    if (isWeather) {
      // Signal WeatherAlmanac to open the modal via ?report=1 — the
      // page also handles the login round-trip when the user is out.
      if (pathname === "/weather") {
        nav("/weather?report=1", { replace: false });
      } else {
        nav("/weather?report=1");
      }
    } else {
      nav("/nature/identify");
    }
  };

  return (
    <div className="fixed z-40 bottom-6 right-6 sm:bottom-8 sm:right-8"
         data-testid="report-contribution-fab">
      {/* Expanded menu */}
      {open && (
        <div className="mb-2 flex flex-col items-end gap-2" data-testid="fab-menu">
          <FabAction label="Report Local Weather" icon={CloudRain}
                     onClick={() => { setOpen(false); nav("/weather?report=1"); }}
                     accent="#0ea5e9" testid="fab-weather" />
          <FabAction label="Identify a plant or animal" icon={Leaf}
                     onClick={() => { setOpen(false); nav("/nature/identify"); }}
                     accent="#16a34a" testid="fab-nature-photo" />
          <FabAction label="Identify a bird call" icon={Mic}
                     onClick={() => { setOpen(false); nav("/nature/identify"); }}
                     accent="#7c3aed" testid="fab-nature-audio" />
          <FabAction label="Log a nature sighting" icon={Sun}
                     onClick={() => { setOpen(false); nav("/nature/observations"); }}
                     accent="#d97706" testid="fab-observation" />
        </div>
      )}

      {/* Primary FAB */}
      <div className="flex items-center gap-2">
        <button type="button" onClick={goPrimary}
                className="pl-4 pr-5 py-3 rounded-full text-white shadow-xl hover:shadow-2xl transition inline-flex items-center gap-2"
                style={{
                  background: isWeather
                    ? "linear-gradient(135deg,#0ea5e9,#0369a1)"
                    : "linear-gradient(135deg,#16a34a,#065f46)",
                }}
                data-testid="fab-primary">
          {(() => { const I = primaryIcon; return <I className="w-5 h-5" />; })()}
          <span className="font-medium">{primaryLabel}</span>
        </button>
        <button type="button" onClick={() => setOpen((v) => !v)}
                className="w-11 h-11 rounded-full text-white shadow-xl hover:shadow-2xl transition flex items-center justify-center"
                style={{ background: "#0f172a" }}
                aria-label={open ? "Close menu" : "More options"}
                data-testid="fab-toggle">
          {open ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
}

function FabAction({ label, icon: Icon, onClick, accent, testid }) {
  return (
    <button type="button" onClick={onClick}
            className="pl-4 pr-5 py-2 rounded-full bg-white shadow-lg border hover:shadow-xl transition inline-flex items-center gap-2 text-sm"
            style={{ borderColor: "rgba(0,0,0,0.08)", color: accent }}
            data-testid={testid}>
      <Icon className="w-4 h-4" style={{ color: accent }} />
      <span className="font-medium">{label}</span>
    </button>
  );
}
